import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  Workflow, 
  Plus, 
  Play, 
  Trash2, 
  Clock,
  MoreHorizontal,
  Loader2,
  Building2,
  Users,
  RefreshCw,
  Download,
  Copy
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import api from "@/services/api";
import { formatDistanceToNow } from "date-fns";

const StageBadge = ({ stage }) => {
  const stageConfig = {
    bronze: { className: "badge-bronze", label: "Bronze" },
    silver: { className: "badge-silver", label: "Silver" },
    gold: { className: "badge-gold", label: "Gold" },
  };
  
  const config = stageConfig[stage] || stageConfig.bronze;
  
  return (
    <Badge variant="outline" className={config.className}>
      {config.label}
    </Badge>
  );
};

const PipelineCard = ({ pipeline, onRunClick, onDelete, onExport, onExportAutomation, onExportSemanticLayer, onExportTextToSQL, onExportCRMTextToSQL, running }) => {
  const stageTypes = [...new Set(pipeline.stages.map(s => s.stage_type))];
  
  return (
    <Card className="shadow-soft hover:shadow-medium transition-all duration-200" data-testid={`pipeline-${pipeline.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-primary/10 rounded-xl">
              <Workflow className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold">{pipeline.name}</h3>
              <p className="text-sm text-muted-foreground">
                {pipeline.stages.length} stage{pipeline.stages.length !== 1 ? "s" : ""}
              </p>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`pipeline-menu-${pipeline.id}`}>
                <MoreHorizontal className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem asChild>
                <Link to={`/pipelines/${pipeline.id}/edit`}>Edit Pipeline</Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => {
                navigator.clipboard.writeText(pipeline.id);
                toast.success("Pipeline ID copied to clipboard");
              }}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Pipeline ID
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExport(pipeline.id, pipeline.name)}>
                <Download className="w-4 h-4 mr-2" />
                Export for Local
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportAutomation(pipeline.id, pipeline.name)}>
                <Download className="w-4 h-4 mr-2" />
                Export Automation SQL
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportSemanticLayer(pipeline.id, pipeline.name)}>
                <Download className="w-4 h-4 mr-2" />
                Export Semantic Layer
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportTextToSQL(pipeline.id, pipeline.name)}>
                <Download className="w-4 h-4 mr-2" />
                Export Text-to-SQL App
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onExportCRMTextToSQL(pipeline.id, pipeline.name)}>
                <Download className="w-4 h-4 mr-2" />
                Export CRM Text-to-SQL App
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive">
                    Delete
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Pipeline</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete &quot;{pipeline.name}&quot;? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => onDelete(pipeline.id)} className="bg-destructive text-destructive-foreground">
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        {/* Stage badges */}
        <div className="flex gap-2 mt-4 flex-wrap">
          {stageTypes.map((stage) => (
            <StageBadge key={stage} stage={stage} />
          ))}
        </div>
        
        {/* Last run info */}
        <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
          <Clock className="w-4 h-4" />
          {pipeline.last_run ? (
            <span>Last run {formatDistanceToNow(new Date(pipeline.last_run), { addSuffix: true })}</span>
          ) : (
            <span>Never run</span>
          )}
        </div>
        
        {/* Actions */}
        <div className="flex gap-2 mt-4 pt-4 border-t">
          <Button 
            variant="default" 
            size="sm" 
            className="flex-1"
            onClick={() => onRunClick(pipeline)}
            disabled={running === pipeline.id}
            data-testid={`run-pipeline-${pipeline.id}`}
          >
            {running === pipeline.id ? (
              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            ) : (
              <Play className="w-4 h-4 mr-1" />
            )}
            Run for Tenant
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default function Pipelines() {
  const [pipelines, setPipelines] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(null);
  
  // Run dialog state
  const [runDialogOpen, setRunDialogOpen] = useState(false);
  const [selectedPipeline, setSelectedPipeline] = useState(null);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [selectedSchemaName, setSelectedSchemaName] = useState("");
  const [runAllSchemas, setRunAllSchemas] = useState(false);
  const [dataStrategy, setDataStrategy] = useState("override"); // override or merge

  // Automation export dialog state
  const [automationDialogOpen, setAutomationDialogOpen] = useState(false);
  const [automationPipeline, setAutomationPipeline] = useState(null);
  const [sourceDb, setSourceDb] = useState("");
  const [destDb, setDestDb] = useState("");
  const [cdcMinutes, setCdcMinutes] = useState("1");
  const [silverGoldMinutes, setSilverGoldMinutes] = useState("5");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [pipelinesData, tenantsData] = await Promise.all([
        api.getPipelines(),
        api.getTenants()
      ]);
      setPipelines(pipelinesData);
      setTenants(tenantsData);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleRunClick = (pipeline) => {
    setSelectedPipeline(pipeline);
    setSelectedTenantId("");
    setSelectedUnitId("");
    setSelectedSchemaName("");
    setRunAllSchemas(false);
    setDataStrategy(pipeline.data_strategy || "override"); // Use pipeline default
    setRunDialogOpen(true);
  };

  const runPipeline = async () => {
    if (!selectedTenantId) {
      toast.error("Please select a tenant");
      return;
    }
    if (!selectedUnitId) {
      toast.error("Please select a unit");
      return;
    }

    setRunning(selectedPipeline.id);
    setRunDialogOpen(false);
    
    try {
      await api.runPipelineWithSchema(
        selectedPipeline.id, 
        selectedTenantId, 
        selectedUnitId, 
        selectedSchemaName || null,
        runAllSchemas,
        dataStrategy
      );
      const message = runAllSchemas 
        ? `Pipeline started for all schemas of unit ${selectedUnitId}`
        : `Pipeline started for tenant: ${selectedTenantId}`;
      toast.success(message);
      fetchData();
    } catch (error) {
      toast.error("Failed to start pipeline");
    } finally {
      setRunning(null);
    }
  };

  const deletePipeline = async (id) => {
    try {
      await api.deletePipeline(id);
      toast.success("Pipeline deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete pipeline");
    }
  };

  const exportPipelineForLocal = async (id, name) => {
    try {
      const exportData = await api.exportPipelineForLocal(id);
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name.replace(/\s+/g, "_").toLowerCase()}_export.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`Pipeline exported: ${name} v${exportData.export_version}`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export pipeline");
    }
  };

  const exportAutomationSQL = async (id, name) => {
    setAutomationPipeline({ id, name });
    setSourceDb("");
    setDestDb("");
    setCdcMinutes("1");
    setSilverGoldMinutes("5");
    setAutomationDialogOpen(true);
  };

  const downloadAutomationSQL = async () => {
    if (!sourceDb.trim() || !destDb.trim()) {
      toast.error("Please enter both source and destination database names");
      return;
    }
    try {
      const response = await api.exportAutomationSQL(
        automationPipeline.id, sourceDb, destDb,
        parseInt(cdcMinutes) || 1, parseInt(silverGoldMinutes) || 5
      );
      
      const blob = new Blob([response], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${automationPipeline.name.replace(/\s+/g, "_").toLowerCase()}_automation.sql`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      setAutomationDialogOpen(false);
      toast.success(`Automation SQL exported for ${automationPipeline.name}`);
    } catch (error) {
      console.error("Export automation error:", error);
      toast.error("Failed to export automation SQL");
    }
  };

  // Semantic Layer export dialog
  const [semanticDialogOpen, setSemanticDialogOpen] = useState(false);
  const [semanticPipeline, setSemanticPipeline] = useState(null);
  const [semanticSourceDb, setSemanticSourceDb] = useState("");
  const [semanticDialect, setSemanticDialect] = useState("sqlserver");

  const exportSemanticLayer = async (id, name) => {
    setSemanticPipeline({ id, name });
    setSemanticSourceDb("");
    setSemanticDialect("sqlserver");
    setSemanticDialogOpen(true);
  };

  const downloadSemanticLayer = async () => {
    if (!semanticSourceDb.trim()) {
      toast.error("Please enter the source database name");
      return;
    }
    try {
      const response = await api.exportSemanticLayer(
        semanticPipeline.id, semanticSourceDb, semanticDialect
      );

      const blob = new Blob([JSON.stringify(response, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${semanticPipeline.name.replace(/\s+/g, "_").toLowerCase()}_semantic_layer.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSemanticDialogOpen(false);
      toast.success(`Semantic Layer exported for ${semanticPipeline.name}`);
    } catch (error) {
      console.error("Export semantic layer error:", error);
      toast.error("Failed to export semantic layer");
    }
  };

  // Text-to-SQL export
  const [textToSqlDialogOpen, setTextToSqlDialogOpen] = useState(false);
  const [textToSqlPipeline, setTextToSqlPipeline] = useState(null);
  const [textToSqlDomain, setTextToSqlDomain] = useState("erp");
  const [textToSqlConfig, setTextToSqlConfig] = useState({
    app_domain: "erp",
    source_database: "",
    sql_server_host: "",
    sql_server_port: "1433",
    sql_server_user: "",
    sql_server_password: "",
    sql_server_database: "",
    helpdesk_url: "https://ultimate.giize.com/api/v1/chat",
    helpdesk_api_key: "",
    service_port: "8000",
    service_name: "UltiDataLakeTextToSQL",
  });

  const openTextToSQLExport = (id, name, domain = "erp") => {
    setTextToSqlPipeline({ id, name });
    setTextToSqlDomain(domain);
    setTextToSqlConfig({
      app_domain: domain,
      source_database: "",
      sql_server_host: "",
      sql_server_port: "1433",
      sql_server_user: "",
      sql_server_password: "",
      sql_server_database: "",
      helpdesk_url: domain === "crm" ? "" : "https://ultimate.giize.com/api/v1/chat",
      helpdesk_api_key: "",
      service_port: "8000",
      service_name: domain === "crm" ? "UltiDataLakeCRMTextToSQL" : "UltiDataLakeTextToSQL",
    });
    setTextToSqlDialogOpen(true);
  };

  const exportTextToSQL = (id, name) => openTextToSQLExport(id, name, "erp");
  const exportCRMTextToSQL = (id, name) => openTextToSQLExport(id, name, "crm");

  const updateTextToSqlConfig = (field, value) => {
    setTextToSqlConfig((current) => ({
      ...current,
      [field]: value,
      ...(field === "source_database" && !current.sql_server_database
        ? { sql_server_database: value }
        : {}),
    }));
  };

  const downloadTextToSQL = async () => {
    if (!textToSqlConfig.source_database.trim()) {
      toast.error("Please enter the source database name");
      return;
    }
    if (!textToSqlConfig.sql_server_host.trim() || !textToSqlConfig.sql_server_user.trim() || !textToSqlConfig.sql_server_database.trim()) {
      toast.error("Please enter SQL Server host, user, and database");
      return;
    }
    try {
      const exportFn = textToSqlDomain === "crm"
        ? api.exportCRMTextToSQLPackage
        : api.exportTextToSQLPackage;
      const response = await exportFn(textToSqlPipeline.id, {
        ...textToSqlConfig,
        app_domain: textToSqlDomain,
        sql_server_port: parseInt(textToSqlConfig.sql_server_port) || 1433,
        service_port: parseInt(textToSqlConfig.service_port) || 8000,
      });
      const blob = new Blob([response], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const prefix = textToSqlDomain === "crm" ? "crm-text-to-sql" : "text-to-sql";
      a.download = `${prefix}-${textToSqlPipeline.name.replace(/\s+/g, "_").toLowerCase()}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setTextToSqlDialogOpen(false);
      toast.success(`${textToSqlDomain === "crm" ? "CRM " : ""}Text-to-SQL package exported for ${textToSqlPipeline.name}`);
    } catch (error) {
      console.error("Export Text-to-SQL error:", error);
      toast.error("Failed to export Text-to-SQL package");
    }
  };

  // Get selected tenant for unit dropdown
  const selectedTenant = tenants.find(t => t.tenant_id === selectedTenantId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loader w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pipelines-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Data Pipelines</h2>
          <p className="text-sm text-muted-foreground">
            Create pipelines once, run for any tenant
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} size="sm">
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </Button>
          <Link to="/pipelines/new">
            <Button data-testid="create-pipeline-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Pipeline
            </Button>
          </Link>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-4">
            <div className="p-2 bg-white rounded-lg shadow-sm">
              <Users className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h3 className="font-semibold">Multi-Tenant Pipeline Execution</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Pipelines are tenant-agnostic. When you click <strong>&quot;Run for Tenant&quot;</strong>, 
                select which tenant (from your CRM) to run the pipeline for. The <code className="bg-white px-1 rounded">tenant_id</code> will be 
                automatically injected into all data.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pipelines Grid */}
      {pipelines.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="empty-state">
            <Workflow className="empty-state-icon" />
            <h3 className="font-semibold text-lg">No Pipelines Yet</h3>
            <p className="text-muted-foreground text-sm mt-1 max-w-sm">
              Create your first data pipeline to start moving data from Oracle to BigQuery
            </p>
            <Link to="/pipelines/new" className="mt-4">
              <Button data-testid="create-first-pipeline">
                <Plus className="w-4 h-4 mr-2" />
                Create Pipeline
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pipelines.map((pipeline) => (
            <PipelineCard
              key={pipeline.id}
              pipeline={pipeline}
              onRunClick={handleRunClick}
              onDelete={deletePipeline}
              onExport={exportPipelineForLocal}
              onExportAutomation={exportAutomationSQL}
              onExportSemanticLayer={exportSemanticLayer}
              onExportTextToSQL={exportTextToSQL}
              onExportCRMTextToSQL={exportCRMTextToSQL}
              running={running}
            />
          ))}
        </div>
      )}

      {/* Run Pipeline Dialog */}
      <Dialog open={runDialogOpen} onOpenChange={setRunDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Play className="w-5 h-5 text-green-600" />
              Run Pipeline for Tenant
            </DialogTitle>
            <DialogDescription>
              Select tenant, unit, and schema(s) to run the pipeline.
            </DialogDescription>
          </DialogHeader>
          
          {selectedPipeline && (
            <div className="space-y-4">
              {/* Pipeline Info */}
              <div className="p-3 bg-muted rounded-lg">
                <p className="text-sm font-medium">Pipeline:</p>
                <p className="text-lg font-semibold">{selectedPipeline.name}</p>
                <div className="flex gap-1 mt-2">
                  {[...new Set(selectedPipeline.stages.map(s => s.stage_type))].map(stage => (
                    <StageBadge key={stage} stage={stage} />
                  ))}
                </div>
              </div>

              {/* Tenant Selection */}
              <div className="space-y-2">
                <Label>Select Tenant (from CRM) *</Label>
                <Select value={selectedTenantId} onValueChange={(val) => {
                  setSelectedTenantId(val);
                  setSelectedUnitId("");
                  setSelectedSchemaName("");
                  setRunAllSchemas(false);
                }}>
                  <SelectTrigger data-testid="run-tenant-select">
                    <SelectValue placeholder="Select tenant..." />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map(t => (
                      <SelectItem key={t.tenant_id} value={t.tenant_id}>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          <span>{t.name}</span>
                          <span className="text-xs text-muted-foreground">({t.tenant_id})</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Unit Selection */}
              <div className="space-y-2">
                <Label>Accounting Unit *</Label>
                <Select 
                  value={selectedUnitId} 
                  onValueChange={(val) => {
                    setSelectedUnitId(val);
                    setSelectedSchemaName("");
                    setRunAllSchemas(false);
                  }}
                  disabled={!selectedTenantId || !selectedTenant?.units?.length}
                >
                  <SelectTrigger data-testid="run-unit-select">
                    <SelectValue placeholder={selectedTenantId ? "Select unit" : "Select tenant first"} />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedTenant?.units?.map(u => (
                      <SelectItem key={u.unit_id} value={u.unit_id}>
                        {u.name || u.unit_id} ({(u.schemas?.length || (u.oracle_schema ? 1 : 0))} schemas)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Schema Selection */}
              {selectedUnitId && selectedUnitId !== "__none__" && (
                <div className="space-y-2">
                  <Label>Oracle Schema</Label>
                  {(() => {
                    const unit = selectedTenant?.units?.find(u => u.unit_id === selectedUnitId);
                    const schemas = unit?.schemas?.length > 0 
                      ? unit.schemas 
                      : (unit?.oracle_schema ? [{ schema_name: unit.oracle_schema, financial_year: null }] : []);
                    
                    if (schemas.length === 0) {
                      return (
                        <p className="text-xs text-yellow-600 p-2 bg-yellow-50 rounded">
                          No schemas configured for this unit. Configure schemas in Tenant Management.
                        </p>
                      );
                    }
                    
                    return (
                      <div className="space-y-2">
                        <Select 
                          value={runAllSchemas ? "__all__" : (selectedSchemaName || "__first__")}
                          onValueChange={(val) => {
                            if (val === "__all__") {
                              setRunAllSchemas(true);
                              setSelectedSchemaName("");
                            } else if (val === "__first__") {
                              setRunAllSchemas(false);
                              setSelectedSchemaName("");
                            } else {
                              setRunAllSchemas(false);
                              setSelectedSchemaName(val);
                            }
                          }}
                        >
                          <SelectTrigger data-testid="run-schema-select">
                            <SelectValue placeholder="Select schema" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__first__">First active schema</SelectItem>
                            <SelectItem value="__all__">🔄 Run ALL schemas ({schemas.filter(s => s.is_active !== false).length})</SelectItem>
                            {schemas.filter(s => s.is_active !== false).map(s => (
                              <SelectItem key={s.schema_name} value={s.schema_name}>
                                {s.schema_name} {s.financial_year && `(FY ${s.financial_year})`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {runAllSchemas && (
                          <p className="text-xs text-blue-600 bg-blue-50 p-2 rounded">
                            Pipeline will run once for each active schema ({schemas.filter(s => s.is_active !== false).length} runs)
                          </p>
                        )}
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Data Flow Preview */}
              {selectedTenantId && selectedUnitId && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-xs text-green-700 font-medium mb-1">Data will be tagged with:</p>
                  <code className="text-sm text-green-800">
                    tenant_id = &apos;{selectedTenantId}&apos;
                    {selectedUnitId && selectedUnitId !== "__none__" && `, unit_id = '${selectedUnitId}'`}
                    {selectedSchemaName && `, schema = '${selectedSchemaName}'`}
                    {runAllSchemas && ` (all schemas)`}
                  </code>
                </div>
              )}

              {/* Data Strategy Selection */}
              {selectedTenantId && selectedUnitId && (
                <div className="space-y-2">
                  <Label>Data Handling Strategy</Label>
                  <div className="flex gap-2">
                    <label className={`flex items-center gap-2 p-2 border rounded cursor-pointer flex-1 ${dataStrategy === 'override' ? 'border-primary bg-primary/5' : 'border-gray-200'}`}>
                      <input 
                        type="radio" 
                        name="runDataStrategy" 
                        value="override" 
                        checked={dataStrategy === 'override'} 
                        onChange={() => setDataStrategy('override')}
                      />
                      <div>
                        <div className="text-sm font-medium">Override</div>
                        <div className="text-xs text-muted-foreground">Replace all data for this tenant/unit</div>
                      </div>
                    </label>
                    <label className={`flex items-center gap-2 p-2 border rounded cursor-pointer flex-1 ${dataStrategy === 'merge' ? 'border-primary bg-primary/5' : 'border-gray-200'}`}>
                      <input 
                        type="radio" 
                        name="runDataStrategy" 
                        value="merge" 
                        checked={dataStrategy === 'merge'} 
                        onChange={() => setDataStrategy('merge')}
                      />
                      <div>
                        <div className="text-sm font-medium">Merge</div>
                        <div className="text-xs text-muted-foreground">Keep other schemas, replace same schema</div>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRunDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={runPipeline} 
              disabled={!selectedTenantId || !selectedUnitId}
              data-testid="confirm-run-pipeline"
            >
              <Play className="w-4 h-4 mr-1" />
              {runAllSchemas ? "Run for All Schemas" : "Run Pipeline"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Automation Export Dialog */}
      <Dialog open={automationDialogOpen} onOpenChange={setAutomationDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Automation SQL</DialogTitle>
            <DialogDescription>
              Generate a complete T-SQL script that sets up everything: CDC, Bronze, Silver (MERGE), Gold, and Agent Jobs. The DBA runs it once in SSMS.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Source Database Name</Label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                placeholder="e.g. EFA100all"
                value={sourceDb}
                onChange={(e) => setSourceDb(e.target.value)}
                data-testid="source-db-input"
              />
              <p className="text-xs text-muted-foreground mt-1">The customer's source database with the original data</p>
            </div>
            <div>
              <Label>Destination Database Name</Label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                placeholder="e.g. AIdb100"
                value={destDb}
                onChange={(e) => setDestDb(e.target.value)}
                data-testid="dest-db-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Empty database where Bronze/Silver/Gold tables will be created</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>CDC Sync (minutes)</Label>
                <input
                  type="number"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  value={cdcMinutes}
                  onChange={(e) => setCdcMinutes(e.target.value)}
                  min="1"
                  data-testid="cdc-minutes-input"
                />
              </div>
              <div>
                <Label>Silver+Gold (minutes)</Label>
                <input
                  type="number"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  value={silverGoldMinutes}
                  onChange={(e) => setSilverGoldMinutes(e.target.value)}
                  min="1"
                  data-testid="silver-gold-minutes-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAutomationDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={downloadAutomationSQL}
              disabled={!sourceDb.trim() || !destDb.trim()}
              data-testid="download-automation-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              Download SQL Script
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Semantic Layer Export Dialog */}
      <Dialog open={semanticDialogOpen} onOpenChange={setSemanticDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Semantic Layer</DialogTitle>
            <DialogDescription>
              Generate a Virtual Gold Semantic Layer — metadata that maps business-friendly names to the source ERP schema. No physical tables, no CDC, no destination database.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Source Database Name</Label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                placeholder="e.g. EFA100all"
                value={semanticSourceDb}
                onChange={(e) => setSemanticSourceDb(e.target.value)}
                data-testid="semantic-source-db-input"
              />
              <p className="text-xs text-muted-foreground mt-1">The source ERP database that AI will query against</p>
            </div>
            <div>
              <Label>SQL Dialect</Label>
              <select
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                value={semanticDialect}
                onChange={(e) => setSemanticDialect(e.target.value)}
                data-testid="semantic-dialect-select"
              >
                <option value="sqlserver">SQL Server (T-SQL)</option>
                <option value="oracle">Oracle (PL/SQL)</option>
              </select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSemanticDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={downloadSemanticLayer}
              disabled={!semanticSourceDb.trim()}
              data-testid="download-semantic-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              Download Semantic Layer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Text-to-SQL Export Dialog */}
      <Dialog open={textToSqlDialogOpen} onOpenChange={setTextToSqlDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              Export {textToSqlDomain === "crm" ? "CRM " : ""}Text-to-SQL App
            </DialogTitle>
            <DialogDescription>
              Download a customer-ready {textToSqlDomain === "crm" ? "CRM " : ""}Text-to-SQL package with credentials and Windows setup scripts included.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <div>
              <Label>Source Database Name</Label>
              <input
                type="text"
                className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                placeholder="e.g. EFA100all"
                value={textToSqlConfig.source_database}
                onChange={(e) => updateTextToSqlConfig("source_database", e.target.value)}
                data-testid="text-to-sql-source-db"
              />
              <p className="text-xs text-muted-foreground mt-1">The customer's source database. The semantic layer will be pre-configured with this name.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>SQL Server Host</Label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  placeholder="127.0.0.1 or server IP"
                  value={textToSqlConfig.sql_server_host}
                  onChange={(e) => updateTextToSqlConfig("sql_server_host", e.target.value)}
                />
              </div>
              <div>
                <Label>SQL Server Port</Label>
                <input
                  type="number"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  placeholder="1433"
                  value={textToSqlConfig.sql_server_port}
                  onChange={(e) => updateTextToSqlConfig("sql_server_port", e.target.value)}
                />
              </div>
              <div>
                <Label>SQL Server Username</Label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  placeholder="sa"
                  value={textToSqlConfig.sql_server_user}
                  onChange={(e) => updateTextToSqlConfig("sql_server_user", e.target.value)}
                />
              </div>
              <div>
                <Label>SQL Server Password</Label>
                <input
                  type="password"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  placeholder="Password"
                  value={textToSqlConfig.sql_server_password}
                  onChange={(e) => updateTextToSqlConfig("sql_server_password", e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <Label>SQL Server Database</Label>
                <input
                  type="text"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  placeholder="e.g. EFA100all"
                  value={textToSqlConfig.sql_server_database}
                  onChange={(e) => updateTextToSqlConfig("sql_server_database", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {textToSqlDomain !== "crm" && (
                <>
                  <div>
                    <Label>Help Desk URL</Label>
                    <input
                      type="text"
                      className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                      value={textToSqlConfig.helpdesk_url}
                      onChange={(e) => updateTextToSqlConfig("helpdesk_url", e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Help Desk API Key</Label>
                    <input
                      type="password"
                      className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                      placeholder="X-API-Key value"
                      value={textToSqlConfig.helpdesk_api_key}
                      onChange={(e) => updateTextToSqlConfig("helpdesk_api_key", e.target.value)}
                    />
                  </div>
                </>
              )}
              <div>
                <Label>Service Port</Label>
                <input
                  type="number"
                  className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
                  value={textToSqlConfig.service_port}
                  onChange={(e) => updateTextToSqlConfig("service_port", e.target.value)}
                />
              </div>
            </div>

            <div className="bg-slate-50 rounded-lg p-3 space-y-1 text-xs text-slate-600">
              <p className="font-medium text-slate-700">Package includes:</p>
              <p>- START_HERE_INSTALL.cmd one-click installer</p>
              <p>- UltiDataLakeTextToSQL.exe when the portal has a prebuilt service binary</p>
              <p>- Semantic Layer JSON (pre-generated)</p>
              <p>- .env with the credentials entered here</p>
              <p>- setup.ps1 / uninstall.ps1 support scripts</p>
              <p>- README and runtime metadata</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setTextToSqlDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={downloadTextToSQL}
              disabled={!textToSqlConfig.source_database.trim()}
              data-testid="download-text-to-sql-btn"
            >
              <Download className="w-4 h-4 mr-1" />
              Download ZIP
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
